/**
 * Ending copy and the snowball-sampling invite — the TypeScript port of the
 * consoles' `webui/static/shared/done.js`.
 *
 * A completion and a *refusal* are not the same event: an interview that
 * reached its end is thanked, while `survey_full` / `quota_denied:*` /
 * `start_failed` get their own title and no ✓ — thanking someone for finishing
 * an interview that never ran reads as a bug to the participant.
 *
 * The invite's display conditions are the server's (feature on, study not yet
 * at capacity): the text arrives non-empty only when it should be shown, via
 * the closing `snowball_sampling` utterance while live or
 * GET /surveys/{id}/branding on a later load. This module never decides, it
 * only renders. The copy itself lives in i18n.ts (`done.*` / `closed.*`), in
 * the interview's language.
 */
import { translate } from "./i18n";

// Normal endings: the interview ran, so the participant is thanked.
const COMPLETION_REASONS = new Set([
  "all_questions_asked",
  "consent_declined",
  "disqualified",
  "global_turn_cap",
]);

// Refusals and errors: no interview happened. `start_failed` comes from the
// workflow's _abort_start; `survey_full` / `quota_denied:*` from the quota
// gate's _deny_start.
const CLOSED_KINDS = new Set(["survey_full", "start_failed", "quota_denied"]);

export interface DoneCopy {
  title: string;
  text: string;
  /** True for the refusal/error endings: no ✓, no thanks-for-finishing. */
  closed: boolean;
}

function closedKind(reason: string): string | null {
  if (!reason) return null;
  if (CLOSED_KINDS.has(reason)) return reason;
  if (reason.startsWith("quota_denied:")) return "quota_denied";
  return null;
}

/** The copy for a terminal reason, in language `code` (default English). */
export function doneCopy(reason = "", code = "en"): DoneCopy {
  const kind = closedKind(reason);
  if (kind) {
    return {
      title: translate(code, `closed.${kind}.title`),
      text: translate(code, `closed.${kind}.text`),
      closed: true,
    };
  }
  return {
    title: translate(code, "done.title"),
    text: translate(code, COMPLETION_REASONS.has(reason) ? `done.${reason}` : "done.text"),
    closed: false,
  };
}

/** True when the reason means the interview was refused or broke, rather than
 *  ended — callers use it to keep a refusal out of the completion record. */
export function isClosedReason(reason = ""): boolean {
  return closedKind(reason) !== null;
}

/** The invite arrives as "message\n\nlink" (repository._snowball_invite).
 *  Split it so the link can be an anchor; a message with no link still
 *  renders. */
export function splitInvite(invite = ""): { message: string; url: string } {
  const lines = invite.trim().split(/\n+/);
  const last = (lines[lines.length - 1] ?? "").trim();
  if (lines.length > 1 && /^https?:\/\/\S+$/.test(last)) {
    return { message: lines.slice(0, -1).join("\n").trim(), url: last };
  }
  return { message: invite.trim(), url: "" };
}
